import React, { useState, useEffect } from "react";

// Класи розмірів
const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-9 w-9",
    lg: "h-12 w-12",
};

// Додаємо https:// якщо немає
function normalizeUrl(url) {
    if (!url) return null;
    if (!/^https?:\/\//i.test(url)) return "https://" + url;
    return url;
}

// Витягуємо домен
function getDomain(url) {
    try {
        return new URL(url).hostname;
    } catch {
        return null;
    }
}

// Створюємо абсолютний /favicon.ico
function getDirectIcoFavicon(url) {
    try {
        return new URL("/favicon.ico", url).href;
    } catch {
        return null;
    }
}

// Створюємо абсолютний /favicon.svg
function getDirectSvgFavicon(url) {
    try {
        return new URL("/favicon.svg", url).href;
    } catch {
        return null;
    }
}

export default function SiteIcon({
    siteUrl,
    logoUrl,
    name,
    size = "md",
    className = "",
    isFolder = false
}) {
    const [loaded, setLoaded] = useState(false);
    const [index, setIndex] = useState(0);
    const [proxyFavicon, setProxyFavicon] = useState(null);

    const normalizedUrl = normalizeUrl(siteUrl);

    useEffect(() => {
        setLoaded(false);
        setIndex(0);
        setProxyFavicon(null);

        if (!normalizedUrl) return;

        // ⚙️ ТУТ ми звертаємось до backend proxy за favicon із HTML
        // Очікуємо, що бекенд повертає щось типу: { faviconUrl: "https://..." }
        (async () => {
            try {
                const res = await fetch(
                    `/api/favicon?url=${encodeURIComponent(normalizedUrl)}`
                );
                if (!res.ok) return;
                const data = await res.json();
                if (data?.faviconUrl) {
                    setProxyFavicon(data.faviconUrl);
                }
            } catch (e) {
                // ігноруємо — це останній пріоритет, не критично
            }
        })();
    }, [normalizedUrl, logoUrl]);

    const domain = getDomain(normalizedUrl);

    const directSvgFavicon = normalizedUrl
        ? getDirectSvgFavicon(normalizedUrl)
        : null;

    const directIcoFavicon = normalizedUrl
        ? getDirectIcoFavicon(normalizedUrl)
        : null;

    const s2Favicon = domain
        ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
        : null;

    const googleFavicon = normalizedUrl
        ? `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&url=${encodeURIComponent(
            normalizedUrl
        )}&size=64`
        : null;

    // Порядок пріоритетів:
    // 1) logoUrl (може бути SVG/PNG/будь-що)
    // 2) /favicon.svg
    // 3) /favicon.ico
    // 4) Google S2
    // 5) gstatic favicon API
    // 6) 🎯 favicon із HTML через backend proxy (останній пріоритет)
    const candidates = [
        logoUrl,
        directSvgFavicon,
        directIcoFavicon,
        s2Favicon,
        googleFavicon,
        proxyFavicon
    ].filter(Boolean);

    if (isFolder) {
        return (
            <div></div>
        );
    }

    const src = candidates[index] ?? null;

    const initials = name?.trim()?.[0]?.toUpperCase() || "🌐";

    // Якщо кандидатів немає — одразу ініціали
    if (!src) {
        return (
            <div
                className={[
                    "flex items-center justify-center rounded-2xl bg-slate-100 text-xs font-medium text-slate-500 shadow-sm dark:bg-slate-800 dark:text-slate-200 select-none",
                    sizeClasses[size] || sizeClasses.md,
                    className
                ].join(" ")}
            >
                {initials}
            </div>
        );
    }

    return (
        <div
            className={[
                "relative flex items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-sm dark:bg-slate-800",
                sizeClasses[size] || sizeClasses.md,
                className
            ].join(" ")}
        >
            {!loaded && (
                <div className="absolute inset-0 animate-pulse bg-slate-200 dark:bg-slate-700" />
            )}

            <img
                src={src}
                alt={name || siteUrl || "Site icon"}
                className={
                    "h-full w-full object-contain transition-opacity duration-150 " +
                    (loaded ? "opacity-100" : "opacity-0")
                }
                onLoad={() => setLoaded(true)}
                onError={() => {
                    setLoaded(false);
                    setIndex((prev) => prev + 1); // ❗️ помилка = картинка не загрузилась → пробуємо наступний варіант
                }}
            />

        </div>
    );
}
